/**
 * R14: WHAT THE INCOMING-CALL SHEET DECIDED, HANDED TO THE CALL IT JOINS.
 *
 * The sheet's mic and camera toggles are the call's own controls, pre-set:
 * "Mic off" joins muted, "Camera on" joins with the camera running, and the
 * preview's camera stream is handed over so the device is not opened twice
 * (the light would blink off and on, and a second permission prompt could
 * appear on some browsers). `PeerAudioRoom` reads the settings when it builds
 * the call and takes the stream when its camera starts.
 *
 * ONE RECORD, for one session. Only its camera STREAM is on a clock: one
 * nobody takes within HANDOFF_TTL_MS is released, so a Join whose
 * navigation never reached the call cannot leave the camera light on. The
 * CHOICE (mic, camera) stays until the call takes it, a new Join replaces
 * it, or it is dropped (#1002 review 7: a slow navigation must never turn
 * "Mic off" into an unmuted join; a camera-on choice whose stream went opens
 * the camera afresh in the call). It names its session, so a leftover never
 * applies to another call.
 */

export interface JoinChoice {
  sessionId: number;
  cameraOn: boolean;
  micOn: boolean;
}

/** How long the preview's STREAM waits for the call: long enough for the route to mount and its camera to start; short enough that a lost hand-off frees the camera soon. */
export const HANDOFF_TTL_MS = 30_000;

let held: (JoinChoice & { stream: MediaStream | null }) | null = null;
let expiry: ReturnType<typeof setTimeout> | null = null;

function stopStream(stream: MediaStream | null): void {
  try {
    stream?.getTracks().forEach((t) => t.stop());
  } catch {
    /* already stopped */
  }
}

function clearTimer(): void {
  if (expiry !== null) {
    clearTimeout(expiry);
    expiry = null;
  }
}

/**
 * Record the choice for `choice.sessionId`, with the preview stream when the
 * camera is on: from here the record owns that stream (a camera-off choice
 * holds none; the sheet releases its own).
 */
export function setJoinChoice(choice: JoinChoice, stream: MediaStream | null): void {
  if (held && held.stream && held.stream !== stream) stopStream(held.stream);
  clearTimer();
  held = { ...choice, stream: choice.cameraOn ? stream : null };
  if (held.stream) expiry = setTimeout(releaseHeldStream, HANDOFF_TTL_MS);
}

/** The hand-off window ran out: the camera goes, the choice stays. */
function releaseHeldStream(): void {
  expiry = null;
  if (!held) return;
  stopStream(held.stream);
  held = { ...held, stream: null };
}

/** The choice for this session, without taking it (the call screen's first render reads it). */
export function peekJoinChoice(sessionId: number | string | null | undefined): JoinChoice | null {
  if (!held || sessionId === null || sessionId === undefined) return null;
  if (String(held.sessionId) !== String(sessionId)) return null;
  return { sessionId: held.sessionId, cameraOn: held.cameraOn, micOn: held.micOn };
}

/**
 * The preview's camera stream for this session, taken: the caller owns it
 * now. Null when there is none, it is for another session, or its track has
 * already ended (the caller then opens the camera itself). The call's camera
 * starting is the record's last reader, so the record goes with it.
 */
export function takeHandedVideo(sessionId: number | string): MediaStream | null {
  if (!held || String(held.sessionId) !== String(sessionId)) return null;
  const stream = held.stream;
  clearTimer();
  held = null;
  const track = stream?.getVideoTracks()[0];
  if (!stream || !track || track.readyState !== 'live') {
    stopStream(stream);
    return null;
  }
  return stream;
}

/**
 * Forget the choice. With a session id, only that session's; without,
 * whatever is held. A stream still held is released (the camera light goes
 * off) unless `release` is false: a Join that failed takes its stream back,
 * still running, for the preview it came from.
 */
export function dropJoinChoice(sessionId?: number | string, release = true): void {
  if (!held) return;
  if (sessionId !== undefined && String(held.sessionId) !== String(sessionId)) return;
  clearTimer();
  const stream = held.stream;
  held = null;
  if (release) stopStream(stream);
}
